import React, { useMemo, useRef, useEffect } from "react";
import { components } from "react-select";
import PCSelect from "./PCSelect";
import { getWeekNumber, normalizeWeekCode, formatDisplayWeek } from "../helper";

/*
 * WeekRangeSelect — searchable dropdown replacement for <input type="week">.
 *
 * Lists a ROLLING window of weeks around today — 7 years back to 5 years forward,
 * oldest first, each shown as "14 Jul 2026 - 20 Jul 2026". Built on PCSelect so it
 * inherits the app's field styling and react-select's type-to-filter search.
 *
 * DROP-IN CONTRACT: the value is the ISO code "YYYY-Www" (same as the native
 * picker) and onChange is called with a synthetic { target: { value } } event, so
 * existing handlers that read evt.target.value keep working unchanged — no data or
 * behaviour change downstream.
 */

/*
 * Size of the window, in years either side of the current week.
 *
 * This used to be a FIXED floor of March 2026 plus 52 weeks ahead, which meant the
 * lower bound never moved and every week before 2026-W10 was unreachable from any
 * picker in the app — even though real data goes back to 2023-W11 (pc_shop_sale) and
 * a live pc_part_master row starts at 2025-W48, so those records could not be edited.
 * The window is now relative to today and rolls forward on its own.
 */
const YEARS_BEFORE_CURRENT = 7;
const YEARS_AFTER_CURRENT = 5;

function mondayOf(date) {
    const dayNr = (date.getDay() + 6) % 7; // Mon=0 … Sun=6
    const monday = new Date(date);
    monday.setDate(date.getDate() - dayNr);
    monday.setHours(0, 0, 0, 0);
    return monday;
}

// Shift `years` from today and snap to that week's Monday. setFullYear can roll a
// 29 Feb onto 1 Mar in a non-leap year; harmless here because we snap to Monday anyway.
function mondayYearsFromNow(years) {
    const d = new Date();
    d.setFullYear(d.getFullYear() + years);
    return mondayOf(d);
}

function startMonday() {
    return mondayYearsFromNow(-YEARS_BEFORE_CURRENT);
}

function endMonday() {
    return mondayYearsFromNow(YEARS_AFTER_CURRENT);
}

// The current week's code, used as the menu's fallback scroll target.
function currentWeekCode() {
    return normalizeWeekCode(getWeekNumber(new Date()));
}

// 7 years back → 5 years forward, oldest first. Deduped by code (~626 options).
function buildWeekOptions() {
    const start = startMonday();
    const end = endMonday();
    const options = [];
    const seen = new Set();
    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 7)) {
        const value = normalizeWeekCode(getWeekNumber(d));
        if (seen.has(value)) continue;
        seen.add(value);
        const label = formatDisplayWeek(value);
        // `search` lets users filter by date fragment, year, or week number ("W28"/"28").
        options.push({ value, label, search: `${label} ${value}` });
    }
    return options;
}

/*
 * Built once per page load and shared by every instance. The list is the same for all
 * of them and is now ~626 options, so rebuilding it per mount is wasted work on screens
 * that render two week fields. Being module-level means the window does not re-roll in a
 * tab left open across a year boundary — the same was true of the previous per-mount
 * useMemo, and an out-of-window value is still surfaced as an extra option (see below).
 */
let cachedWeekOptions = null;
function getWeekOptions() {
    if (cachedWeekOptions === null) cachedWeekOptions = buildWeekOptions();
    return cachedWeekOptions;
}

// Match the typed input against the option's date range, year, and week code.
function filterOption(option, rawInput) {
    if (!rawInput) return true;
    const haystack = (option.data.search || option.label || "").toLowerCase();
    return haystack.includes(rawInput.toLowerCase());
}

// Menu list that centres the selected week when the menu opens. With the list
// running oldest-first, the current week sits near the bottom, so we scroll it
// into view instead of leaving the menu parked at the top.
function MenuList(props) {
    const { innerRef } = props;
    const listRef = useRef(null);
    const setRef = (el) => {
        listRef.current = el;
        if (typeof innerRef === "function") innerRef(el);
        else if (innerRef) innerRef.current = el;
    };
    useEffect(() => {
        const list = listRef.current;
        if (!list) return;
        /*
         * Centre the selected week — or, when the field is EMPTY, the current week.
         *
         * The empty case matters since the window widened to 7 years back: the list now starts
         * in 2019, so without this the menu opened seven years in the past and every user had to
         * scroll forward to reach today.
         */
        let target = list.querySelector('[aria-selected="true"]');
        if (!target) {
            const code = currentWeekCode();
            target = list.querySelector(`[data-week="${code}"]`)
                || [...list.querySelectorAll('[role="option"]')].find(o => (o.textContent || '').trim() === formatDisplayWeek(code));
        }
        if (!target) return;
        const listRect = list.getBoundingClientRect();
        const selRect = target.getBoundingClientRect();
        list.scrollTop += (selRect.top - listRect.top) - list.clientHeight / 2 + selRect.height / 2;
    }, []);
    return <components.MenuList {...props} innerRef={setRef} />;
}

export default function WeekRangeSelect({ value, onChange, disabled, isDisabled, style, className, ...rest }) {
    const baseOptions = useMemo(getWeekOptions, []);
    const normValue = normalizeWeekCode(value);

    // Resolve the currently selected option; if the stored week falls outside the
    // rolling window, surface it as an extra option so the field is never blank and the
    // value is preserved untouched on save.
    let options = baseOptions;
    let selected = normValue ? baseOptions.find(o => o.value === normValue) || null : null;
    if (!selected && normValue) {
        const label = formatDisplayWeek(normValue) || normValue;
        selected = { value: normValue, label, search: `${label} ${normValue}` };
        options = [selected, ...baseOptions];
    }

    const handleChange = (opt) => {
        if (onChange) onChange({ target: { value: opt ? opt.value : "" } });
    };

    const select = (
        <PCSelect
            options={options}
            value={selected}
            onChange={handleChange}
            isDisabled={disabled || isDisabled}
            filterOption={filterOption}
            components={{ MenuList }}
            placeholder="Select week"
            {...rest}
        />
    );

    // Preserve any inline width/layout the native input carried at the call site.
    if (style || className) {
        return <div style={style} className={className}>{select}</div>;
    }
    return select;
}
